// AIDialer.jsx — AI Dialer v1 hub (Day 4 rebuild).
//
// Tabs (Campaigns is the default):
//   1. Campaigns    — v1 campaigns list + quick actions (start/pause/edit/delete)
//   2. Live Monitor — real-time in-flight calls across all campaigns
//   3. Settings     — per-agent dialer settings (cell forward, default voice)
//   4. Shadow Mode  — existing v0 shadow-mode tool (preserved from PR #117)
//
// All routes are wrapped at the App.jsx level by the AI_DIALER feature flag.

import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Phone, Plus, Play, Pause, Loader2, Bot, AlertCircle,
  BarChart3, PhoneCall, Sparkles, Radio, EyeOff, Settings as SettingsIcon,
  Trash2, Edit3, ChevronRight, DollarSign, Volume2, BookOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiDel, apiPatch } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import ShadowMode from './ShadowMode.jsx';
import CostBadge from '../components/dialer/CostBadge.jsx';
import ListenInPlayer from '../components/dialer/ListenInPlayer.jsx';
import ConfirmModal from '../components/dialer/ConfirmModal.jsx';
import ScreenersList from './AIDialerScreeners.jsx';
import { getSocket } from '../lib/socket.js';
import { primaryBot } from '../lib/dialerHelpers.js';

const TABS = [
  { id: 'campaigns',   label: 'Campaigns',    icon: PhoneCall },
  { id: 'live',        label: 'Live Monitor', icon: Radio },
  { id: 'screeners',   label: 'Screeners',    icon: Bot },
  { id: 'settings',    label: 'Settings',     icon: SettingsIcon },
  { id: 'shadow-mode', label: 'Shadow Mode',  icon: EyeOff },
];
const TAB_IDS = TABS.map((t) => t.id);
const DEFAULT_TAB = 'campaigns';

const STOCK_VOICES = [
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (warm, male)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (friendly, female)' },
  { id: 'CYw3kZ02Hs0563khs1Fj', label: 'Dave (calm, male)' },
  { id: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda (energetic, female)' },
];

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     bg: 'bg-white/5',       color: 'text-white/60',   dot: 'bg-white/50' },
  active:    { label: 'Active',    bg: 'bg-green-400/10',  color: 'text-green-400',  dot: 'bg-green-400' },
  paused:    { label: 'Paused',    bg: 'bg-yellow-400/10', color: 'text-yellow-300', dot: 'bg-yellow-400' },
  completed: { label: 'Completed', bg: 'bg-cyan-500/10',   color: 'text-civix-cyan', dot: 'bg-civix-cyan' },
  queued:    { label: 'Queued',    bg: 'bg-white/5',       color: 'text-white/60',   dot: 'bg-white/50' },
  dialing:   { label: 'Dialing',   bg: 'bg-blue-500/10',   color: 'text-blue-300',   dot: 'bg-blue-400' },
  in_progress:      { label: 'In progress',  bg: 'bg-green-400/10', color: 'text-green-300', dot: 'bg-green-400' },
  qualifying:       { label: 'Qualifying',   bg: 'bg-purple-500/10', color: 'text-civix-purple', dot: 'bg-civix-purple' },
  awaiting_handoff: { label: 'Awaiting handoff', bg: 'bg-yellow-400/10', color: 'text-yellow-300', dot: 'bg-yellow-400' },
  transferring:     { label: 'Transferring',  bg: 'bg-cyan-500/10', color: 'text-civix-cyan', dot: 'bg-civix-cyan' },
};

function StatusPill({ status }) {
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${sc.bg} ${sc.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
      {sc.label}
    </span>
  );
}

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
        active
          ? 'bg-civix-purple/20 text-white border border-civix-purple/40'
          : 'text-white/50 hover:text-white hover:bg-white/5 border border-transparent'
      }`}
    >
      <Icon className="w-4 h-4" />
      {tab.label}
    </button>
  );
}

// ── Campaigns tab ────────────────────────────────────────────────────────
function CampaignsTab() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState(null); // { id, name } | null

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dialer-v1-campaigns'],
    queryFn:  () => apiGet('/dialer/v1/campaigns'),
    refetchInterval: 15000,
  });
  const campaigns = data?.data || [];

  const startMut  = useMutation({
    mutationFn: (id) => apiPost(`/dialer/v1/campaigns/${id}/start`, {}),
    onSuccess: () => { toast.success('Campaign started'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Start failed'),
  });
  const pauseMut  = useMutation({
    mutationFn: (id) => apiPost(`/dialer/v1/campaigns/${id}/pause`, {}),
    onSuccess: () => { toast.success('Campaign paused'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Pause failed'),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => apiDel(`/dialer/v1/campaigns/${id}`),
    onSuccess: () => {
      toast.success('Campaign deleted');
      setPendingDelete(null);
      refetch();
      qc.invalidateQueries({ queryKey: ['dialer-v1-campaigns'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  if (isLoading) {
    return <div className="panel p-12 flex justify-center"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold text-white">Your Dialer Campaigns</div>
          <div className="text-xs text-white/50">Each campaign uses an AI screener to qualify and transfer to you.</div>
        </div>
        <button
          onClick={() => nav('/ai-dialer/campaigns/new')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="panel p-10 text-center">
          <Bot className="w-12 h-12 text-civix-purple/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
          <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
            Train your first AI screener or jump straight into a campaign with a pre-built persona.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => nav('/ai-dialer?tab=screeners')} className="btn-secondary flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Train an AI screener
            </button>
            <button onClick={() => nav('/ai-dialer/campaigns/new')} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Campaign
            </button>
          </div>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campaigns.map((c) => {
            const s = c.stats || {};
            return (
              <div key={c._id} className="panel p-5 hover:border-civix-purple/30 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-white truncate">{c.name}</h3>
                      <StatusPill status={c.status} />
                    </div>
                    <div className="text-xs text-white/40">
                      Persona: {c.persona?.name} · Voice: {STOCK_VOICES.find((v) => v.id === c.persona?.voiceId)?.label || c.persona?.voiceId || '—'}
                    </div>
                  </div>
                  <CostBadge spent={s.spentUsd || 0} cap={c.budget?.dailyCapUsd || 0} />
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <Stat label="Dialed"      value={s.dialed || 0} />
                  <Stat label="Answered"    value={s.answered || 0} />
                  <Stat label="Qualified"   value={s.qualified || 0} />
                  <Stat label="Transferred" value={s.transferred || 0} />
                  <Stat label="Booked"      value={s.booked || 0} />
                  <Stat label="Spent"       value={`$${Number(s.spentUsd || 0).toFixed(2)}`} />
                </div>

                <div className="flex items-center justify-end gap-2">
                  {c.status === 'draft' && (
                    <>
                      <button
                        onClick={() => setPendingDelete({ id: c._id, name: c.name })}
                        className="btn-sm btn-ghost text-red-400 flex items-center gap-1"
                        title="Delete this draft campaign"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                      <button onClick={() => startMut.mutate(c._id)} className="btn-sm btn-primary flex items-center gap-1">
                        <Play className="w-3 h-3" /> Start
                      </button>
                    </>
                  )}
                  {c.status === 'active' && (
                    <button onClick={() => pauseMut.mutate(c._id)} className="btn-sm btn-ghost text-yellow-400 flex items-center gap-1">
                      <Pause className="w-3 h-3" /> Pause
                    </button>
                  )}
                  {c.status === 'paused' && (
                    <button onClick={() => startMut.mutate(c._id)} className="btn-sm btn-primary flex items-center gap-1">
                      <Play className="w-3 h-3" /> Resume
                    </button>
                  )}
                  <button onClick={() => nav(`/ai-dialer/campaign/${c._id}`)} className="btn-sm btn-ghost flex items-center gap-1">
                    View calls <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {pendingDelete && (
          <ConfirmModal
            title="Delete this campaign?"
            body={
              <>
                You're about to permanently delete{' '}
                <span className="text-white">"{pendingDelete.name}"</span>. This can't be undone — campaign stats and any past calls under it will stay archived under your account, but the draft itself will be gone.
              </>
            }
            confirmLabel="Delete campaign"
            danger
            busy={deleteMut.isPending}
            onConfirm={() => deleteMut.mutate(pendingDelete.id)}
            onCancel={() => !deleteMut.isPending && setPendingDelete(null)}
          />
        )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

// ── Live Monitor tab ──────────────────────────────────────────────────────
function LiveMonitorTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['dialer-v1-live'],
    queryFn:  () => apiGet('/dialer/v1/live'),
    refetchInterval: 3000,
  });
  const [presence, setPresence] = useState({}); // callId → last socket payload
  const [listening, setListening] = useState(null); // { callId, leadName } | null

  // Subscribe to socket presence updates (best-effort — falls back to polling).
  useEffect(() => {
    const socket = getSocket();
    if (!socket?.on) return undefined;
    const onUpdate = (p) => {
      if (!p?.callId) return;
      setPresence((m) => ({ ...m, [p.callId]: p }));
    };
    socket.on('ai-dialer:call-update', onUpdate);
    return () => { socket.off?.('ai-dialer:call-update', onUpdate); };
  }, []);

  if (isLoading) {
    return <div className="panel p-12 flex justify-center"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>;
  }

  const calls = data?.data || [];

  if (!calls.length) {
    return (
      <div className="panel p-12 text-center">
        <Radio className="w-12 h-12 text-civix-purple/40 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No active calls</h3>
        <p className="text-white/50 text-sm max-w-md mx-auto">
          When a campaign is live, in-flight calls stream here in real time — including the AI transcript and a one-click listen-in.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-white/40 uppercase tracking-wider px-1">
        {calls.length} in-progress AI call{calls.length !== 1 ? 's' : ''}
      </div>
      {calls.map((c) => {
        const lead = c.lead || {};
        const camp = c.campaign || {};
        const live = presence[c._id] || {};
        return (
          <div key={c._id} className="panel p-4">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-green-400/20 flex items-center justify-center">
                <Phone className="w-4 h-4 text-green-400 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">
                  {(lead.firstName || '') + ' ' + (lead.lastName || '') || lead.phone || 'Unknown'}
                </div>
                <div className="text-xs text-white/40 flex items-center gap-3 flex-wrap">
                  <span>{lead.state || '—'}</span>
                  <span>·</span>
                  <span>{camp.name || '—'}</span>
                  <span>·</span>
                  <StatusPill status={c.status} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CostBadge
                  spent={live.spentUsd ?? c.costUsd ?? 0}
                  cap={camp.budget?.perCallCapUsd || 2}
                />
                <button
                  className="btn-sm btn-ghost"
                  onClick={() => setListening({
                    callId: c._id,
                    leadName: (lead.firstName || '') + ' ' + (lead.lastName || '') || lead.phone || 'lead',
                  })}
                  disabled={!!listening}
                >
                  Listen-in
                </button>
              </div>
            </div>
            {live.transcript && (
              <div className="mt-3 text-xs text-white/60 bg-white/5 rounded-lg p-2 max-h-32 overflow-y-auto">
                {live.transcript}
              </div>
            )}
          </div>
        );
      })}
      {listening && (
        <ListenInPlayer
          callId={listening.callId}
          leadName={listening.leadName}
          onClose={() => setListening(null)}
        />
      )}
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────
function SettingsTab() {
  const qc = useQueryClient();
  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn:  () => apiGet('/auth/me'),
  });
  const { data: botsData } = useQuery({
    queryKey: ['dialer-v1-bots'],
    queryFn:  () => apiGet('/dialer/v1/bots'),
  });
  const agent = meData?.data?.agent || meData?.agent || {};
  const ds = agent.dialerSettings || {};
  const bots = botsData?.data || [];
  const ownBot = primaryBot(bots);
  // If the agent's own bot uses a custom (cloned) voice, surface it as an
  // extra option in the dropdown so we can preselect it.
  const stockIds = new Set(STOCK_VOICES.map((v) => v.id));
  const ownCustomVoice = (ownBot?.voiceId && !stockIds.has(ownBot.voiceId))
    ? { id: ownBot.voiceId, label: `${ownBot.name || 'Your bot'} — cloned voice` }
    : null;
  const voiceOptions = ownCustomVoice ? [ownCustomVoice, ...STOCK_VOICES] : STOCK_VOICES;

  const [form, setForm] = useState({
    cellForwardEnabled: false,
    cellPhone: '',
    defaultVoiceId: 'pNInz6obpgDQGcFmaJgB',
  });
  // Once the user touches the voice picker, server/bot defaults stop
  // overriding their choice.
  const userTouchedVoiceRef = useRef(false);

  useEffect(() => {
    setForm((prev) => ({
      cellForwardEnabled: !!ds.cellForwardEnabled,
      cellPhone: ds.cellPhone || '',
      defaultVoiceId: userTouchedVoiceRef.current
        ? prev.defaultVoiceId
        : (ds.defaultVoiceId || 'pNInz6obpgDQGcFmaJgB'),
    }));
  }, [agent._id]);

  // Once bots load, seed the default voice from the agent's primary bot —
  // but only if the agent hasn't saved a server-side preference yet and the
  // user hasn't manually changed the picker this session.
  useEffect(() => {
    if (userTouchedVoiceRef.current) return;
    if (ds.defaultVoiceId) return;
    if (!ownBot?.voiceId) return;
    setForm((f) => ({ ...f, defaultVoiceId: ownBot.voiceId }));
  }, [ownBot?._id, ownBot?.voiceId, ds.defaultVoiceId]);

  const saveMut = useMutation({
    mutationFn: (patch) => apiPatch('/dialer/v1/agent/me/dialer-settings', patch),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['me'] }); },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const e164ok = !form.cellPhone || /^\+[1-9]\d{6,14}$/.test(form.cellPhone);

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="panel p-5 space-y-4">
        <div>
          <div className="text-sm font-semibold text-white mb-1">Cell forward</div>
          <div className="text-xs text-white/50">
            When the AI Dialer qualifies a lead, your CIVIX softphone rings.
            If you check the box, your cell will also ring with caller ID "CIVIX AI Transfer".
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.cellForwardEnabled}
            onChange={(e) => setForm((f) => ({ ...f, cellForwardEnabled: e.target.checked }))}
          />
          <span className="text-sm text-white">Forward warm transfers to my cell phone</span>
        </label>
        <div>
          <label className="form-label" htmlFor="dialer-cell-phone">Cell phone (E.164)</label>
          <input
            id="dialer-cell-phone"
            className={`input w-full ${!e164ok ? 'border-red-500/60' : ''}`}
            placeholder="+15551234567"
            value={form.cellPhone}
            onChange={(e) => setForm((f) => ({ ...f, cellPhone: e.target.value }))}
            aria-invalid={!e164ok}
            aria-describedby="dialer-cell-phone-hint"
          />
          <div id="dialer-cell-phone-hint" className={`text-xs mt-1 ${!e164ok ? 'text-red-400' : 'text-white/40'}`}>
            {!e164ok
              ? 'Must be E.164 format (e.g. +15551234567 — include country code and no spaces).'
              : 'Include the country code, no spaces or dashes. We never display this to leads.'}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => saveMut.mutate({
              cellForwardEnabled: form.cellForwardEnabled,
              cellPhone: form.cellPhone,
              defaultVoiceId: form.defaultVoiceId,
            })}
            disabled={!e164ok || saveMut.isPending}
            className="btn-primary flex items-center gap-2"
            title={!e164ok ? 'Fix the phone number first' : undefined}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SettingsIcon className="w-4 h-4" />}
            Save settings
          </button>
        </div>
      </div>

      <div className="panel p-5 space-y-4">
        <div>
          <div className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-civix-cyan" /> Default voice
          </div>
          <div className="text-xs text-white/50">Used as the default for new campaigns. Per-campaign voice still wins.</div>
        </div>
        <select
          className="input w-full max-w-sm"
          value={form.defaultVoiceId}
          onChange={(e) => {
            userTouchedVoiceRef.current = true;
            setForm((f) => ({ ...f, defaultVoiceId: e.target.value }));
          }}
        >
          {voiceOptions.map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
        {(ds.trainedVoices || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Your trained voices</div>
            <div className="grid grid-cols-2 gap-2">
              {(ds.trainedVoices || []).map((v) => (
                <div key={v.voiceId} className="rounded-lg border border-white/10 px-3 py-2">
                  <div className="text-sm text-white">{v.name || 'Cloned voice'}</div>
                  <div className="text-xs text-white/40 truncate">{v.voiceId}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="panel p-5">
        <div className="text-sm font-semibold text-white mb-2">Your spend</div>
        <UsageBadge />
      </div>

      <TestCallCard />
    </div>
  );
}

// ── Settings → Test Call (Day 5) ───────────────────────────────────────────
function TestCallCard() {
  const [phone, setPhone] = useState('');
  const [botId, setBotId] = useState('');
  const [result, setResult] = useState(null);
  // Track manual picker changes so re-renders after bots load (or after the
  // query refetches in the background) don't clobber the user's choice.
  const userTouchedBotRef = useRef(false);

  const { data: botsData } = useQuery({
    queryKey: ['dialer-v1-bots'],
    queryFn: () => apiGet('/dialer/v1/bots'),
  });
  const bots = botsData?.data || [];
  const ownBot = primaryBot(bots);

  useEffect(() => {
    if (userTouchedBotRef.current) return;
    if (ownBot?._id) setBotId(ownBot._id);
  }, [ownBot?._id]);

  const placeMut = useMutation({
    mutationFn: () => apiPost('/dialer/v1/test-call', { phone, botId: botId || null }),
    onSuccess: (r) => {
      setResult(r?.data);
      toast.success(r?.data?.simulated ? 'Test call simulated (no Twilio creds)' : 'Test call dialed');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Test call failed'),
  });
  const e164ok = /^\+[1-9]\d{6,14}$/.test(phone);

  return (
    <div className="panel p-5 space-y-4">
      <div>
        <div className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
          <Phone className="w-4 h-4 text-civix-cyan" /> Test Call
        </div>
        <div className="text-xs text-white/50">
          Dial one phone number with the AI dialer. Useful for end-to-end live verification.
        </div>
      </div>
      <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-300 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          This dials a real phone number using real Twilio + OpenAI + ElevenLabs.
          Estimated cost: $2 max per call (hard cap).
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">Phone (E.164)</label>
          <input
            className={`input w-full ${phone && !e164ok ? 'border-red-500/60' : ''}`}
            placeholder="+15551234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Bot</label>
          <select
            className="input w-full"
            value={botId}
            onChange={(e) => {
              userTouchedBotRef.current = true;
              setBotId(e.target.value);
            }}
          >
            <option value="">Default Adam persona</option>
            {bots.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          className="btn-primary flex items-center gap-2"
          onClick={() => placeMut.mutate()}
          disabled={!e164ok || placeMut.isPending}
        >
          {placeMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
          Start Test Call
        </button>
      </div>
      {result && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70 space-y-1">
          <div>Call ID: <code className="text-civix-cyan">{result.callId}</code></div>
          {result.callSid && <div>Twilio SID: <code>{result.callSid}</code></div>}
          {result.simulated && <div className="text-yellow-300">Simulated — set TWILIO_* env vars for real dialing.</div>}
          <div>Per-call cap: ${Number(result.perCallCapUsd || 2).toFixed(2)}</div>
        </div>
      )}
    </div>
  );
}

function UsageBadge() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-dialer-usage-summary'],
    queryFn: () => apiGet('/ai-dialer/usage-summary'),
    refetchInterval: 30_000,
  });
  const u = data?.data;
  if (isLoading || !u) {
    return (
      <div className="flex items-center gap-2 text-xs text-white/40">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching your usage…
      </div>
    );
  }
  return (
    <CostBadge
      mode="full"
      label="Per-agent AI Dialer spend (this month)"
      spent={u.currentMonthSpend || 0}
      cap={u.monthlySpendCap || 50}
      sub={`~$${Number(u.costPerMin || 0.07).toFixed(2)}/min during active calls`}
    />
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function AIDialer() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(tabParam) ? tabParam : DEFAULT_TAB;
  const setActiveTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  // Twilio Voice status surfaces the same warning Day 1 had — non-fatal.
  const { data: statusData } = useQuery({
    queryKey: ['ai-dialer-status'],
    queryFn:  () => apiGet('/ai-dialer/status').catch(() => null),
    staleTime: 60000,
  });
  const stub = statusData?.data && !statusData.data.twilioConfigured;

  return (
    <div className="space-y-4">
      <TopBar
        title="AI Dialer"
        subtitle="Manage campaigns, monitor live calls, configure your screener — all in one place."
        actions={
          (activeTab === 'campaigns' || activeTab === 'screeners') ? (
            <button onClick={() => nav('/ai-dialer/bots/train')} className="btn-secondary flex items-center gap-2 text-sm">
              <BookOpen className="w-4 h-4" /> Train a bot
            </button>
          ) : null
        }
      />

      {stub && (
        <div className="panel p-3 border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-300 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Twilio not configured — calls are simulated. Set TWILIO_* env vars to enable live dialing.
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <TabButton key={t.id} tab={t} active={activeTab === t.id} onClick={() => setActiveTab(t.id)} />
        ))}
      </div>

      <div>
        {activeTab === 'campaigns'   && <CampaignsTab />}
        {activeTab === 'live'        && <LiveMonitorTab />}
        {activeTab === 'screeners'   && <ScreenersList embedded />}
        {activeTab === 'settings'    && <SettingsTab />}
        {activeTab === 'shadow-mode' && <ShadowMode />}
      </div>
    </div>
  );
}
